import { CalendarClock, CalendarDays, FileCheck, FilePlus2, Repeat, Users } from "lucide-react"
import { monto as comoMonto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { type MontoDivisa, TarjetaKpi } from "@/componentes/ui/TarjetaKpi"
import { comoFecha } from "@/lib/fechas"

export type TotalPorDivisa = {
  divisa: string
  gastos: string
  nomina: string
  total: string
  pagos: number
}

export type Vencimiento = {
  fecha: string
  concepto: string
  origen: "GASTO" | "NOMINA"
  monto: string
  divisa: string
  referencia_id: number
  /** La de la nómina (`SEMANAL`…): con la fecha dice qué período generar. */
  frecuencia?: string | null
  /** Si se ofrece «Generar»: un gasto, sólo su próximo vencimiento. */
  generable?: boolean
  /** Nómina cuyo período ya está aprobado. */
  generado?: boolean
}

/** La clave de una fila de la agenda: la misma con la que se pinta. */
export const claveDe = (v: Vencimiento) => `${v.origen}-${v.referencia_id}-${v.fecha}`

/** Los tramos que se ofrecen para «Próximos N días». El servidor no acepta otros. */
export const DIAS_AGENDA = [7, 15, 30] as const
export type DiasAgenda = (typeof DIAS_AGENDA)[number]

export type Generada = {
  orden_id: number
  codigo: string
  concepto: string
  vence_en: string
  periodo: string
}

export type IndicadoresProgramados = {
  mes: string
  incluye_nomina: boolean
  del_mes: TotalPorDivisa[]
  por_delante: TotalPorDivisa[]
  pagos_del_mes: number
  pagos_por_delante: number
  dias_agenda: number
  programaciones_activas: number
  programaciones_pausadas: number
  contratos_activos: number
  generadas_del_mes: number
  generadas: Generada[]
  calendario: Vencimiento[]
  agenda: Vencimiento[]
}

const MESES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
]

const aMontos = (filas: TotalPorDivisa[]): MontoDivisa[] =>
  filas.map((f) => ({ divisa: f.divisa, monto: f.total }))

/**
 * El tablero de lo que se repite: cuánto compromete el mes, qué viene y qué ya se generó.
 *
 * ⚠ **El compromiso del mes son las FECHAS de verdad, no el monto mensualizado.** Un
 * sueldo semanal de 200 cuesta 1.000 en un mes con cinco sábados y 800 en uno con
 * cuatro; «200 × 4,33» no es ninguno de los dos. Lo cuenta el servidor con las mismas
 * funciones que usa el beat para generar, así que la tarjeta no puede discrepar de lo
 * que va a aparecer en la bandeja.
 *
 * ⚠ **Los montos van por moneda y no hay «total».** Sumar bolívares con USDT daría una
 * cifra en una unidad que no existe.
 *
 * ⚠ **Si los sueldos no están dentro, la tarjeta lo DICE.** Sin `nomina:ver` el
 * servidor no los suma, y un «compromiso del mes» que significa una cosa para dirección
 * y otra para quien administra gastos sería peor que no enseñarlo.
 */
export function TableroProgramados({
  datos,
  onAbrir,
  onDias,
}: {
  datos: IndicadoresProgramados
  /** Qué detalle pedir. Ninguna tarjeta se queda sin poder abrirse. */
  onAbrir: (que: "mes" | "porDelante" | "generadas" | "series") => void
  /** Cambiar el tramo de «Próximos N días» (12/10). Sin él, no se ofrece. */
  onDias?: (dias: DiasAgenda) => void
}) {
  const mes = MESES[Number(datos.mes.slice(5, 7)) - 1] ?? ""

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <TarjetaKpi
          titulo={`Compromiso de ${mes}`}
          detalle="Lo que vence este mes, contado fecha a fecha"
          Icono={CalendarDays}
          color="var(--info)"
          cantidad={
            <>
              {datos.pagos_del_mes}{" "}
              <span className="text-sm font-normal" style={{ color: "var(--fg-muted)" }}>
                {datos.pagos_del_mes === 1 ? "pago" : "pagos"}
              </span>
            </>
          }
          montos={aMontos(datos.del_mes)}
          pie={
            datos.incluye_nomina
              ? "Pulsa para ver cuáles"
              : "No incluye los sueldos: piden «nomina:ver». Pulsa para ver cuáles."
          }
          onClick={() => onAbrir("mes")}
        />
        <TarjetaKpi
          titulo={`Próximos ${datos.dias_agenda} días`}
          detalle="Desde hoy, lo que hay que tener listo"
          Icono={CalendarClock}
          color="var(--warning)"
          cantidad={
            <>
              {datos.pagos_por_delante}{" "}
              <span className="text-sm font-normal" style={{ color: "var(--fg-muted)" }}>
                {datos.pagos_por_delante === 1 ? "pago" : "pagos"}
              </span>
            </>
          }
          montos={aMontos(datos.por_delante)}
          pie="Pulsa para ver cuáles"
          onClick={() => onAbrir("porDelante")}
          accion={
            onDias && (
              // El tramo junto al icono del calendario, como texto y no como botones
              // (pedido el 12/10). Ver `TarjetaKpi.accion`. Siguen siendo `button` con
              // `aria-pressed`: lo que cambia es el aspecto, no lo que son.
              <fieldset className="flex items-center gap-0.5">
                <legend className="sr-only">Tramo de «Próximos»</legend>
                {DIAS_AGENDA.map((d) => {
                  const elegido = datos.dias_agenda === d
                  return (
                    <button
                      key={d}
                      type="button"
                      aria-pressed={elegido}
                      aria-label={`${d} días`}
                      onClick={() => onDias(d)}
                      className={`foco-cristal min-h-6 rounded px-1 text-xs font-semibold tabular-nums ${elegido ? "underline underline-offset-4" : ""}`}
                      style={{ color: elegido ? "var(--primary-texto)" : "var(--fg-muted)" }}
                    >
                      {d}D
                    </button>
                  )
                })}
              </fieldset>
            )
          }
        />
        <TarjetaKpi
          titulo="Órdenes generadas"
          detalle={`Borradores que salieron de aquí en ${mes}`}
          Icono={FileCheck}
          color="var(--success)"
          cantidad={datos.generadas_del_mes}
          pie="Nacen en BORRADOR: nada se aprueba ni se paga solo. Pulsa para verlas."
          onClick={() => onAbrir("generadas")}
        />
        <TarjetaKpi
          titulo="Lo programado"
          detalle="Series vivas que vigila el sistema"
          Icono={Repeat}
          color="var(--fg-muted)"
          cantidad={datos.programaciones_activas + datos.contratos_activos}
          pie={
            <span className="flex flex-wrap gap-x-3">
              <span>{datos.programaciones_activas} de gastos</span>
              {datos.incluye_nomina && (
                <span className="inline-flex items-center gap-1">
                  <Users size={12} aria-hidden="true" />
                  {datos.contratos_activos} de nómina
                </span>
              )}
              {datos.programaciones_pausadas > 0 && (
                <span>{datos.programaciones_pausadas} en pausa</span>
              )}
              <span>pulsa para verlas</span>
            </span>
          }
          onClick={() => onAbrir("series")}
        />
      </div>
    </div>
  )
}

/**
 * Lo que viene, día a día.
 *
 * ⚠ **Es una PREVISIÓN, no una bandeja**: estas filas todavía no son órdenes —el beat
 * las redacta con su antelación—, así que no se pulsan ni se marcan. Decirlo evita que
 * alguien las busque en la bandeja de órdenes y crea que se perdieron.
 *
 * ⚠ **Va aparte del tablero y con ALTURA LIMITADA, a propósito.** Con cinco series
 * vivas son veinte filas en treinta días, y a pantalla completa empujaba las planillas
 * y la tabla fuera de la vista: lo que se lee de un vistazo era una lista que nadie
 * recorre entera. Comparte fila con las planillas y se desplaza DENTRO de su caja,
 * nunca la página.
 */
export function AgendaProgramados({
  datos,
  className,
  onGenerar,
  puedeGenerar,
  generando,
}: {
  datos: IndicadoresProgramados
  className?: string
  /** «Generar» esa fila ya (10/10): adelantar un pago o no esperar a las 8:15. */
  onGenerar?: (v: Vencimiento) => void
  /** Si quien mira puede generar ESA fila (gasto o nómina piden permisos distintos). */
  puedeGenerar?: (v: Vencimiento) => boolean
  /** La clave de la fila que se está generando. */
  generando?: string | null
}) {
  return (
    <div className={className}>
      <Agenda
        datos={datos}
        onGenerar={onGenerar}
        puedeGenerar={puedeGenerar}
        generando={generando}
      />
    </div>
  )
}

function Agenda({
  datos,
  onGenerar,
  puedeGenerar,
  generando,
}: {
  datos: IndicadoresProgramados
  onGenerar?: (v: Vencimiento) => void
  puedeGenerar?: (v: Vencimiento) => boolean
  generando?: string | null
}) {
  if (datos.agenda.length === 0) {
    return (
      <div className="tarjeta p-3 text-sm" style={{ color: "var(--fg-muted)" }}>
        No vence nada en los próximos {datos.dias_agenda} días.
      </div>
    )
  }

  return (
    <div className="tarjeta p-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-2">
        <h2 className="text-sm font-semibold">Lo que viene</h2>
        <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
          {datos.agenda.length < datos.pagos_por_delante
            ? `${datos.agenda.length} de ${datos.pagos_por_delante}`
            : datos.agenda.length}{" "}
          en {datos.dias_agenda} días · previsión
        </span>
      </div>
      {/* Se desplaza DENTRO de la caja: una lista de veinte filas no puede mover la
          página entera para enseñar lo que hay debajo. */}
      <ul
        className="barra-area max-h-72 divide-y overflow-y-auto"
        style={{ borderColor: "var(--cristal-filo)" }}
      >
        {datos.agenda.map((v) => (
          <li
            key={claveDe(v)}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-sm"
          >
            <span className="w-24 shrink-0 tabular-nums" style={{ color: "var(--fg-muted)" }}>
              {comoFecha(v.fecha)}
            </span>
            <span className="min-w-0 flex-1 truncate">{v.concepto}</span>
            <Insignia tono={v.origen === "NOMINA" ? "info" : "neutro"}>
              {v.origen === "NOMINA" ? "Nómina" : "Gasto"}
            </Insignia>
            <span className="tabular-nums">
              {comoMonto(v.monto)} <span style={{ color: "var(--fg-muted)" }}>{v.divisa}</span>
            </span>
            {v.generado ? (
              <Insignia tono="exito">Generada</Insignia>
            ) : (
              v.generable &&
              onGenerar &&
              (puedeGenerar?.(v) ?? true) && (
                <Boton
                  tamano="sm"
                  disabled={generando != null}
                  onClick={() => onGenerar(v)}
                  aria-label={`Generar ${v.concepto} del ${comoFecha(v.fecha)}`}
                >
                  <FilePlus2 size={14} aria-hidden={true} />
                  {generando === claveDe(v) ? "Generando…" : "Generar"}
                </Boton>
              )
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
